import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { mutate } from "swr";
import { Card, CardContent } from "@mui/material";
import useGlobalContext from "../../hooks/useGlobalContext";
import PageTitleSubtitle from "../../components/PageTitleSubTitle";
import SearcherAndAdd from "../../components/SearcherAndAdd";
import PagePagination from "../../components/PagePagination";
import localize from "../../common/localize";
import ActionButtons from "../../components/common/ActionButtons";
import ModalBackground from "../../components/common/ModalBackground";
import {
  getClearFinishConcept,
  getCurrencyFromSetup,
  getFinishConceptFilter,
} from "../../common/EntitiesManagement";
import ErrorMessage from "../../components/common/ErrorMessage";
import DeleteConfirmationMessage from "../../components/common/DeleteConfirmationMessage";
import TableComponent from "../../components/common/TableComponent";
import {
  generateNoDataValues,
  getFiltersInfo,
  getValueInCurrency,
} from "../../common/utils";
import ValueOrNoDataLabel from "../../components/common/ValueOrNoDataLabel";
import EntityPrinter from "../../components/common/EntityPrinter";
import AddOrEditFinishConcept from "./components/AddOrEditFinishConcept";
import TrueFalseImage from "../../components/TrueFalseImage";

export default function FinishConcept() {
  const {
    finishConceptModel,
    setFinishConceptModel,
    finishConceptData,
    FINISH_CONCEPT_ENDPOINT,
    config,
    pageIndexFinishConcept,
    setPageIndexFinishConcept,
    toggleVisibleModalConfirm,
    deleteEntity,
    createEntity,
    updateEntity,
    basicFilterData,
    setBasicFilterOptions,
  } = useGlobalContext();
  const { id } = useParams();
  const [finishConceptDataFiltered, setFinishConceptDataFiltered] = useState(
    finishConceptData.Data,
  );
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showPrinterModal, setShowPrinterModal] = useState(false);

  useEffect(() => {
    setBasicFilterOptions(getFinishConceptFilter());
  }, []);

  useEffect(() => {
    if (id) {
      setShowEditModal(true);
      const operationalDataObj = finishConceptData.Data.find(
        (operationalAreaObj) => operationalAreaObj.Id === Number(id),
      );
      setFinishConceptModel(operationalDataObj);
    }
  }, [id]);

  useEffect(() => {
    setFinishConceptDataFiltered(finishConceptData.Data);
  }, [finishConceptData]);

  const validateOperationResult = (result, isDelete) => {
    setFinishConceptModel(getClearFinishConcept());

    if (!result.ok) {
      setErrorMessage(JSON.stringify(result.data));
      setShowErrorModal(true);
      return;
    }

    mutate([FINISH_CONCEPT_ENDPOINT, config]);
    if (isDelete) {
      if (
        pageIndexFinishConcept !== 0 &&
        finishConceptModel.Data.length === 1
      ) {
        setPageIndexFinishConcept(pageIndexFinishConcept - 1);
      }
    }
    window.location.reload(false);
  };

  const handleDeleteRequest = async (itemId) => {
    const deleteResult = await deleteEntity(
      `${`${FINISH_CONCEPT_ENDPOINT}/${itemId}`}`,
      config,
    );
    toggleVisibleModalConfirm();
    validateOperationResult(deleteResult, true);
  };

  const handleUpdateRequest = async (data) => {
    const updateResult = await updateEntity(
      `${`${FINISH_CONCEPT_ENDPOINT}/${data.Id}`}`,
      data,
      config,
    );
    validateOperationResult(updateResult, false);
  };

  const handleCreateRequest = async (data) => {
    const updateResult = await createEntity(
      FINISH_CONCEPT_ENDPOINT,
      data,
      config,
    );
    validateOperationResult(updateResult, false);
  };

  const disableSaveChanges = () =>
    !finishConceptModel?.Name ||
    finishConceptModel?.Name.replaceAll(" ", "").length <= 0;

  const getColumnTitles = () => {
    return [
      localize.name,
      localize.replace_service_value,
      localize.value,
      localize.description,
      localize.modification_date,
      localize.modified_by,
    ];
  };

  return (
    <div className="lateral_padding">
      {showErrorModal && (
        <ErrorMessage
          message={errorMessage}
          title={localize.error_unexpected}
          showModal={showErrorModal}
          setShowModal={setShowErrorModal}
        />
      )}

      {showPrinterModal && (
        <EntityPrinter
          closeCallback={() => setShowPrinterModal(false)}
          report_title={localize.finish_concept}
          report_subtitle={getFiltersInfo(basicFilterData)}
          url={FINISH_CONCEPT_ENDPOINT}
          filters={basicFilterData ?? []}
          columns={getColumnTitles()}
          objectTypeName="finish_concept"
        />
      )}

      {showEditModal && (
        <ModalBackground
          isDrawerMode
          hasRequiredValues
          showModal={showEditModal}
          setShowModal={setShowEditModal}
          title={localize.edit_finish_concept}
          onClickToSubmit={() => {
            handleUpdateRequest(finishConceptModel);
            setShowEditModal(false);
            setFinishConceptModel(getClearFinishConcept());
          }}
          onClickToCancel={() => {
            setShowEditModal(false);
            setFinishConceptModel(getClearFinishConcept());
          }}
          Component={
            <AddOrEditFinishConcept
              model={finishConceptModel}
              setModel={setFinishConceptModel}
            />
          }
          validators={disableSaveChanges()}
        />
      )}

      <Card
        variant="outlined"
        style={{
          margin: "16px",
          boxShadow:
            "rgba(60, 64, 67, 0.3) 0px 1px 2px 0px, rgba(60, 64, 67, 0.15) 0px 1px 3px 1px",
        }}
      >
        <CardContent>
          <PageTitleSubtitle
            title={localize.finish_concept}
            subtitle={localize.finish_concept_description}
          />

          <SearcherAndAdd
            printButtonMethod={() => {
              setShowPrinterModal(true);
            }}
            entityName="finish_concept"
            handleAddBtn={() => setShowAddModal(true)}
            modal={
              <ModalBackground
                isDrawerMode
                hasRequiredValues
                showModal={showAddModal}
                setShowModal={setShowAddModal}
                title={localize.new_finish_concept}
                onClickToSubmit={() => {
                  handleCreateRequest(finishConceptModel);
                  setShowAddModal(false);
                  setFinishConceptModel(getClearFinishConcept());
                }}
                onClickToCancel={() => {
                  setShowAddModal(false);
                  setFinishConceptModel(getClearFinishConcept());
                }}
                Component={
                  <AddOrEditFinishConcept
                    model={finishConceptModel}
                    setModel={setFinishConceptModel}
                  />
                }
                validators={disableSaveChanges()}
              />
            }
          />
        </CardContent>
      </Card>

      <DeleteConfirmationMessage
        entityName={finishConceptModel.Name}
        entityType={localize.finish_concept}
        onClickForCancel={() => {
          toggleVisibleModalConfirm();
          setFinishConceptModel(getClearFinishConcept());
        }}
        onClickForConfirm={() => {
          handleDeleteRequest(finishConceptModel.Id);
          setFinishConceptModel(getClearFinishConcept());
        }}
      />

      <TableComponent
        pagination={
          <PagePagination
            pageIndex={pageIndexFinishConcept}
            setPageIndex={setPageIndexFinishConcept}
            dataArray={finishConceptData}
          />
        }
        firstColumnTitle={localize.actions}
        columnTitles={getColumnTitles()}
        results={
          finishConceptData.Data?.length !== 0
            ? finishConceptDataFiltered.map((item) => ({
                key: item.Id,
                data: [
                  <div>
                    <ActionButtons
                      onClickForEditButton={() => {
                        setFinishConceptModel(item);
                        setShowEditModal(true);
                      }}
                      onclickForDeleteButton={() => {
                        setFinishConceptModel(item);
                        toggleVisibleModalConfirm();
                      }}
                    />
                  </div>,
                  <ValueOrNoDataLabel data={item.Name} />,
                  <TrueFalseImage value={item.ReplaceServiceValue} />,
                  <ValueOrNoDataLabel
                    data={getValueInCurrency(
                      getCurrencyFromSetup(),
                      item.Value,
                    )}
                  />,
                  <ValueOrNoDataLabel data={item.Description} />,
                  new Date(item.ModificationDate).toLocaleString(undefined, {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  }),
                  item.CreatedBy,
                ],
              }))
            : [generateNoDataValues(7)]
        }
      />
    </div>
  );
}
